'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  listOfflineChapters,
  groupChaptersByObra,
  removeOfflineChapter as dbRemoveOfflineChapter,
  getOfflinePageBlob,
  hasOfflineChapter as dbHasOfflineChapter,
  saveOfflineChapter,
  saveOfflineCover,
  type OfflineChapterMeta,
} from '@/utils/offline-db';
import { api, API_CONFIG, unwrapApiResponse } from '@/utils/api';

export interface DownloadQueueItem extends OfflineChapterMeta {
  addedAt: number;
}

export type DownloadPhase = 'metadata' | 'images' | 'indexing' | null;

interface UseOfflineChaptersOptions {
  onChapterDownloaded?: (chapterName: string) => void;
}

function buildChapterPagesJsonUrl(capitulo: any): string | null {
  const obraId = capitulo?.obra?.id ?? capitulo?.obra?.obr_id ?? capitulo?.obr_id;
  const uuid = capitulo?.cap_uuid ?? capitulo?.uuid;
  const num = capitulo?.cap_num ?? capitulo?.numero;
  if (obraId == null || !uuid || num == null) return null;
  const segment = String(num).replace('.', '_');
  return `${API_CONFIG.CDN_URL}/obras/${obraId}/capitulos/${segment}/${uuid}.json`;
}

function toAbsoluteUrl(raw: string): string {
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  return `${API_CONFIG.CDN_URL}/${raw.replace(/^\/+/, '')}`;
}

function pageEntryToSrc(pagina: any): string | null {
  if (!pagina) return null;
  if (typeof pagina === 'string') {
    const v = pagina.trim();
    return v ? toAbsoluteUrl(v) : null;
  }
  const candidate = String(pagina.url ?? pagina.src ?? '').trim();
  if (!candidate) return null;
  return toAbsoluteUrl(candidate);
}

async function fetchChapterPagesArray(capitulo: any): Promise<any[]> {
  const jsonUrl = buildChapterPagesJsonUrl(capitulo);
  if (!jsonUrl) return [];
  try {
    const res = await fetch(jsonUrl, { cache: 'no-store' });
    if (!res.ok) return [];
    const payload = await res.json();
    return Array.isArray(payload) ? payload : [];
  } catch {
    return [];
  }
}

function normalizeChapterType(raw: any): 'imagem' | 'texto' {
  const t = String(raw ?? 'imagem').toLowerCase();
  return t === 'imagem' ? 'imagem' : 'texto';
}

function normalizeTextMode(raw: any): 'texto' | 'markdown' | 'html' {
  const t = String(raw ?? '').toLowerCase();
  if (t === 'markdown') return 'markdown';
  if (t === 'html') return 'html';
  return 'texto';
}

export function useOfflineChapters(options?: UseOfflineChaptersOptions) {
  const [chapters, setChapters] = useState<OfflineChapterMeta[]>([]);
  const [loading, setLoading] = useState(true);

  // Queue and Progress state
  const [downloadQueue, setDownloadQueue] = useState<DownloadQueueItem[]>([]);
  const [downloadingChapterId, setDownloadingChapterId] = useState<number | null>(null);
  const [downloadingChapterName, setDownloadingChapterName] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);
  const [downloadPhase, setDownloadPhase] = useState<DownloadPhase>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await listOfflineChapters();
      setChapters(list);
    } catch (e) {
      console.error('Erro ao listar capítulos offline:', e);
      setChapters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const removeChapter = useCallback(
    async (chapterId: number) => {
      await dbRemoveOfflineChapter(chapterId);
      await refresh();
    },
    [refresh]
  );

  const hasOfflineChapter = useCallback(async (chapterId: number) => {
    return dbHasOfflineChapter(chapterId);
  }, []);

  const getPageBlobUrl = useCallback(async (chapterId: number, pageIndex: number): Promise<string | null> => {
    const blob = await getOfflinePageBlob(chapterId, pageIndex);
    return blob ? URL.createObjectURL(blob) : null;
  }, []);

  const enqueueDownload = useCallback((meta: OfflineChapterMeta) => {
    setDownloadQueue((prev) => {
      if (prev.some((item) => item.chapterId === meta.chapterId)) return prev;
      return [...prev, { ...meta, addedAt: Date.now() }];
    });
  }, []);

  // Worker effect to process the queue
  useEffect(() => {
    if (downloadQueue.length === 0 || downloadingChapterId !== null) return;

    const next = downloadQueue[0];
    const processDownload = async () => {
      setDownloadingChapterId(next.chapterId);
      setDownloadingChapterName(next.chapterName);
      setDownloadProgress({ current: 0, total: 0 });
      setDownloadPhase('metadata');

      try {
        // 1. Fetch full chapter data (including image URLs)
        const chapterData = unwrapApiResponse<any>(await api.get(`capitulos/${next.chapterId}`));
        const chapterType = normalizeChapterType(chapterData?.tipo ?? chapterData?.cap_tipo);
        const textMode = normalizeTextMode(chapterData?.tipo ?? chapterData?.cap_tipo);
        const textContent = String(chapterData?.cap_conteudo_texto ?? chapterData?.conteudo_texto ?? '').trim();
        const blobs: Blob[] = [];

        if (chapterType === 'texto') {
          if (!textContent) throw new Error('Capítulo de texto sem conteúdo');
          setDownloadPhase('indexing');
          await saveOfflineChapter(
            {
              ...next,
              chapterType: 'texto',
              textMode,
              textContent,
              pageCount: 0,
              savedAt: Date.now(),
            },
            []
          );
          if (options?.onChapterDownloaded) {
            options.onChapterDownloaded(next.chapterName);
          }
          await refresh();
          return;
        }

        const pagesFromJson = await fetchChapterPagesArray(chapterData);
        const pages = pagesFromJson.length > 0
          ? pagesFromJson
          : Array.isArray(chapterData?.paginas2) && chapterData.paginas2.length > 0
            ? chapterData.paginas2
            : Array.isArray(chapterData?.paginas)
              ? chapterData.paginas
              : [];
        if (pages.length === 0) throw new Error('Nenhuma página encontrada');

        // 2. Download each page image
        setDownloadPhase('images');
        setDownloadProgress({ current: 0, total: pages.length });

        for (let i = 0; i < pages.length; i++) {
          const page = pages[i];
          const url = pageEntryToSrc(page);
          if (!url) {
            throw new Error(`Página ${i + 1} inválida para download`);
          }

          const res = await fetch(url);
          if (!res.ok) {
            throw new Error(`Falha ao baixar página ${i + 1}`);
          }
          const blob = await res.blob();
          blobs.push(blob);
          setDownloadProgress({ current: i + 1, total: pages.length });
        }

        // 3. Save cover if it exists and isn't saved yet
        if (next.obraImage) {
          try {
            const coverRes = await fetch(next.obraImage);
            const coverBlob = await coverRes.blob();
            await saveOfflineCover(next.obraId, coverBlob);
          } catch (coverError) {
            console.warn('Erro ao salvar capa offline:', coverError);
          }
        }

        // 4. Index in IndexedDB
        setDownloadPhase('indexing');
        await saveOfflineChapter(
          {
            ...next,
            chapterType: 'imagem',
            textMode: undefined,
            textContent: undefined,
            pageCount: blobs.length,
            savedAt: Date.now(),
          },
          blobs
        );

        // Success
        if (options?.onChapterDownloaded) {
          options.onChapterDownloaded(next.chapterName);
        }
        await refresh();
      } catch (error) {
        console.error('Erro no download do capítulo:', error);
      } finally {
        setDownloadQueue((prev) => prev.filter((item) => item.chapterId !== next.chapterId));
        setDownloadingChapterId(null);
        setDownloadingChapterName(null);
        setDownloadProgress(null);
        setDownloadPhase(null);
      }
    };

    processDownload();
  }, [downloadQueue, downloadingChapterId, refresh, options]);

  const obrasGrouped = useMemo(() => groupChaptersByObra(chapters), [chapters]);

  return {
    chapters,
    obrasGrouped,
    loading,
    refresh,
    removeChapter,
    hasOfflineChapter,
    getPageBlobUrl,
    enqueueDownload,
    downloadQueue,
    downloadingChapterId,
    downloadingChapterName,
    downloadProgress,
    downloadPhase,
  };
}
